import "core-js/modules/esnext.set.difference.v2.js";
import "core-js/modules/esnext.set.intersection.v2.js";
import "core-js/modules/esnext.set.is-disjoint-from.v2.js";
import "core-js/modules/esnext.set.is-subset-of.v2.js";
import "core-js/modules/esnext.set.is-superset-of.v2.js";
import "core-js/modules/esnext.set.symmetric-difference.v2.js";
import "core-js/modules/esnext.set.union.v2.js";
import { relativeTime } from "../../util/dates.mjs";
const mediaCenterGetters = {
  /* props used in MediaCenter */
  mediaCenterProps(state, getters) {
    return {
      showImageViewer: state.imageZoom.showZoom,
      imageViewerProps: {
        mediaType: getters.imageViewerType,
        imageZoomProps: getters.imageZoomProps,
        ugcImageViewerProps: getters.ugcImageViewerProps,
        videoProps: getters.imageViewerVideoProps,
        showArrows: getters.computedMediaCenterImages.length > 1
      },
      mediaCenterGridProps: getters.mediaCenterGridProps
    };
  },
  /**
   * getters.computedCurrentImage
   * TODO: PDM-3630 - versions for thumbnail vs hero
   *
   * holds a reference to the current selected image object
   * returns state if a user has selected an image
   * returns default image for color if not
   *
   * @param {object} state.media.currentImage current image selected by user
   * @param {array} getters.computedMediaCenterImages default image for color
   * @returns {object} image object
   */
  computedCurrentImage(state, getters) {
    if (state.media.currentImage.uri) return state.media.currentImage;
    if (getters.computedMediaCenterImages[0]) {
      return getters.computedMediaCenterImages[0];
    }
    return {};
  },
  /**
   * getters.computedVideos
   * TODO: Controller ticket PDM-3630, then cleanup here
   *
   * @param {Array<Object>} state.product.videos array of video objects
   *   containing placeholder hero image / thumbnails, embed codes, etc
   * @param {String} getters.appPropsMap.scriptPaths.youTube: URI for youTube embed API
   * @returns {Array<Object>} formatted video objects used for both media carousel
   *   thumbnails, hero images, and video implementation
   */
  computedVideos(state, getters) {
    return state.product.videos.map((video) => {
      video.longDescription = video.description;
      video.description = video.title;
      video.altText = video.title;
      video.caption = video.title;
      video.uri = video.heroImageUrl;
      video.id = video.embedCode;
      video.src = `${getters.appPropsMap.scriptPaths.youTube}${video.embedCode}`;
      video.type = "video";
      video.thumbnailUri = video.thumbnailUrl;
      return video;
    });
  },
  /**
   * getters.computedImages
   *
   * @param {Array<Object>} state.product.images array of image objects
   * @returns {Array<Object>} formatted image objects containing alt text and captions
   */
  computedImages(state, getters) {
    const getTitle = (image) => {
      const trimmedTitle = image.title ? image.title.trim() : "";
      return trimmedTitle.toUpperCase() !== "NONE" ? trimmedTitle : "";
    };
    const getColorLabel = (label) => label && label.toUpperCase() !== "NONE" ? label : "";
    const getBackupAltText = (image) => {
      const colorName = getColorLabel(image.color.displayLabel);
      return colorName && image.type ? `${image.type} of color ${colorName}` : "";
    };
    const transformCaption = (caption) => caption.replace("info:", "").replace("Height", "height").replace("Wearing size", "Size");
    return state.product.images.filter((image) => image.type !== "Feature Banner Image").map((image) => {
      let caption;
      if (image.type === "Swatch Image") {
        caption = getColorLabel(image.color.displayLabel);
      } else if (image.caption) {
        caption = transformCaption(image.caption);
      } else {
        caption = getTitle(image);
      }
      const altText = image.altText || getTitle(image) || getBackupAltText(image);
      const baseUri = `${image.uri}.jpg`;
      const zoomUri = `${image.uri}`;
      return {
        altText,
        baseUri,
        caption,
        color: image.color,
        description: image.description,
        featured: image.featured,
        heroImageUri: `${zoomUri}?size=2000`,
        id: image.id,
        maxHeight: image.maxHeight,
        maxWidth: image.maxWidth,
        size: image.size,
        thumbnailUrl: image.thumbnailUrl,
        thumbnailUri: `${baseUri}?size=60x60`,
        title: image.title,
        type: image.type,
        uri: `${baseUri}${getters.appPropsMap.textStrings.horizontalImageParam}`,
        xlBreakpointUri: `${baseUri}?size=1500`
      };
    });
  },
  computedImagesForColor(state, getters) {
    var _a, _b, _c;
    const displayColor = getters.computedSelectedColor.code || ((_c = (_b = (_a = state.product.displayOptions) == null ? void 0 : _a.featuredImage) == null ? void 0 : _b.color) == null ? void 0 : _c.code);
    return getters.imagesByColorCodeMap[displayColor] ?? [];
  },
  /**
   * getters.computedMediaCenterImages
   * Media Center (used on selection) set of images for current carousel
   *
   * @params from getters:
   * @param {string} computedSelectedColor.code: color code for currently selected color
   * @param {Array<Object>} computedImages: state.product.images with additional data added
   * @param {Array<Object>} computedVideos: state.product.videos formatted for
   *   consistency with image objects
   * @param {Array<Object>} ugcImages: image objects from the UGC service
   * @returns {Array<Object>} subset of image array filtered for the selected color or the color
   * with the most images if no color is selected.
   */
  computedMediaCenterImages(state, getters) {
    const {
      computedImagesForColor,
      computedVideos,
      ugcImages,
      getUgcImageExperience
      // TO-DO: cleanup PDX-5768
    } = getters;
    const imagesForColor = computedImagesForColor.filter((image) => image.type !== "Location Image");
    const ugcSlices = getUgcImageExperience === 0 ? ugcImages.slice(0, 4) : [];
    return [imagesForColor[0], ...computedVideos.slice(0, 1), ...imagesForColor.slice(1), ...ugcSlices, ...computedVideos.slice(1)];
  },
  /**
   * getters.imagesByColorCodeMap
   *
   * @param {Array<Object>} state.product.colors: Array of available colors
   * @param {Array<Object>} getters.computedImages: state.product.images with additional data added
   * @returns {Object<Array>} Arrays of images keyed by color codes
   */
  imagesByColorCodeMap(state, getters) {
    var _a, _b, _c, _d, _e, _f, _g;
    const enableComputedImages = (_b = (_a = getters.appPropsMap) == null ? void 0 : _a.launchToggles) == null ? void 0 : _b.enableComputedImages;
    const images = enableComputedImages ? (_c = state.product) == null ? void 0 : _c.computedImages : (_d = state.product) == null ? void 0 : _d.images;
    const computedImagesSet = new Set(images == null ? void 0 : images.map((img) => {
      var _a2;
      return (_a2 = img == null ? void 0 : img.color) == null ? void 0 : _a2.code;
    }));
    const colorCodes = getters.isPup ? [(_g = (_f = (_e = state.product.displayOptions) == null ? void 0 : _e.featuredImage) == null ? void 0 : _f.color) == null ? void 0 : _g.code] : computedImagesSet;
    const imageColorMap = {};
    const computedImages = enableComputedImages ? state.product.computedImages : getters.computedImages;
    colorCodes.forEach((code) => {
      imageColorMap[code] = computedImages == null ? void 0 : computedImages.filter((image) => image.color && image.color.code === code);
    });
    return imageColorMap;
  },
  heroImageProps(state, getters) {
    return {
      layout: state.product.displayOptions.carouselOrientation,
      images: getters.computedMediaCenterImages,
      currentImage: getters.computedCurrentImage,
      verticalAnimation: state.media.useVerticalAnimation,
      sizeParameter: getters.appPropsMap.textStrings.horizontalImageParam,
      videoLinkName: "thumbnail video play",
      useJpgExt: getters.appPropsMap.triageToggles.jpgExt
    };
  },
  imageZoomProps(state, getters) {
    return {
      imageSrc: getters.computedCurrentImage.heroImageUri,
      imageCaption: getters.computedCurrentImage.caption,
      touchLib: state.imageZoom.library
    };
  },
  ugcImages(state) {
    var _a;
    if (!((_a = state.ugcImageResponse.UgcData) == null ? void 0 : _a.images)) return [];
    return state.ugcImageResponse.UgcData.images.map((entry) => ({
      altText: `@${entry.source.user.userName}'s instagram image`,
      caption: "",
      thumbnailUri: entry.photo.smallSquare,
      uri: entry.photo.largeSquare,
      heroImageUri: entry.photo.extraLargeSquare,
      type: "UGC Image",
      id: entry.id,
      products: entry.products,
      user: entry.source.user,
      likes: entry.source.likeCount,
      postedDate: relativeTime(entry.source.postedTimestamp),
      currentProductId: state.product.styleId
    }));
  },
  ugcSelectedProduct(state, getters) {
    var _a;
    return state.currentUgcProduct || ((_a = getters.computedCurrentImage.products) == null ? void 0 : _a.find((product) => product.metadata.productId === state.product.styleId));
  },
  ugcSelectedImageTileProps(state, getters) {
    const {
      metadata,
      name,
      price,
      reviewStatistic,
      link,
      images
    } = getters.ugcSelectedProduct;
    return {
      src: images[0].small,
      brand: metadata.brand,
      link,
      name,
      price,
      productId: metadata.productId,
      reviewSummary: {
        rating: reviewStatistic == null ? void 0 : reviewStatistic.averageOverallRating,
        count: reviewStatistic == null ? void 0 : reviewStatistic.totalReviewCount
      }
    };
  },
  ugcImageViewerProps(state, getters) {
    const {
      altText,
      id,
      heroImageUri,
      likes,
      type,
      postedDate,
      products,
      user
    } = getters.computedCurrentImage;
    return type === "UGC Image" ? {
      id,
      imageSrc: heroImageUri,
      likes,
      postedDate,
      products,
      selectedProduct: getters.ugcSelectedImageTileProps,
      title: altText,
      user
    } : null;
  },
  imageViewerVideoProps(state, getters) {
    const {
      computedCurrentImage: image
    } = getters;
    return image.type === "video" ? {
      description: image.longDescription,
      imageSrc: image.uri,
      isFocusable: true,
      videoSrc: image.src,
      title: image.title
    } : null;
  },
  imageViewerType(state, getters) {
    if (getters.imageViewerVideoProps) return "video";
    if (getters.ugcImageViewerProps) return "ugc";
    return "image";
  },
  mediaCenterGridProps(state, getters) {
    return {
      images: getters.computedMediaCenterImages,
      showArrows: getters.computedMediaCenterImages.length > 1,
      heroImageProps: {
        currentImage: getters.computedCurrentImage,
        sizeParameter: getters.appPropsMap.textStrings.horizontalImageParam,
        videoLinkName: "thumbnail video play",
        useJpgExt: getters.appPropsMap.triageToggles.jpgExt
      },
      showXlBreakpoint: getters.appPropsMap.launchToggles.enableXlBreakpoint,
      showMoreImagesButton: getters.meetsMoreImagesExpThreshold
    };
  },
  meetsMoreImagesExpThreshold(state, getters) {
    const images = getters.computedMediaCenterImages;
    const typeMap = {
      video: 0,
      ugc: 0,
      product_image: 0
    };
    images.forEach((image) => {
      if (image.type.toLowerCase() === "video") {
        typeMap.video = 1;
      } else if (image.type.toLowerCase() === "ugc image") {
        typeMap.ugc = 1;
      } else {
        typeMap.product_image += 1;
      }
    });
    return typeMap.video === 1 ? typeMap.product_image > 4 : typeMap.product_image > 6;
  }
};
export {
  mediaCenterGetters as default
};
//# sourceMappingURL=mediaCenterGetters.mjs.map
